import { API_ENDPOINTS, API_CONFIG, ML_API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';

// Types for agents API response
export interface Agent {
  name: string;
  description: string;
  agent_id: string;
  parameters: {
    type: string;
    properties: Record<string, any>;
  };
}

export interface AgentsResponse {
  status: string;
  message: string;
  agents: Agent[];
}

// Function to convert agent name to displayable format
export const formatAgentName = (name: string): string => {
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Service for agents API calls
export const agentsService = {
  // Fetch all agents
  async getAllAgents(): Promise<Agent[]> {
    try {
      // Get the bearer token from localStorage
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      
      const response = await fetch(`${API_CONFIG.ML_API_BASE_URL}${ML_API_ENDPOINTS.AGENTS.GET_AGENTS}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: AgentsResponse = await response.json();
      
      if (data.status === 'success') {
        return data.agents;
      } else {
        throw new Error(data.message || 'Failed to fetch agents');
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      throw error;
    }
  },

  // Get agent count
  async getAgentCount(): Promise<number> {
    try {
      const agents = await this.getAllAgents();
      return agents.length;
    } catch (error) {
      console.error('Error getting agent count:', error);
      return 0;
    }
  },

  // Get formatted agent names
  async getFormattedAgentNames(): Promise<string[]> {
    try {
      const agents = await this.getAllAgents();
      return agents.map(agent => formatAgentName(agent.name));
    } catch (error) {
      console.error('Error getting formatted agent names:', error);
      return [];
    }
  },
};

export default agentsService;
